(() => {
  const recipesPath = '/blogs/recipes';
  let isLoading = false;

  const getArticlesGrid = () => document.querySelector('#main-blog-articles .blog-articles');
  const getArticles = () => Array.from(document.querySelectorAll('#main-blog-articles .blog-articles__article'));
  const getOptions = () => Array.from(document.querySelectorAll('#BlogRecipeSortBy input[name="sort_by"]'));
  const getCurrentSortBy = () => new URL(window.location.href).searchParams.get('sort_by') || 'popular';
  const formatRecipeCount = (count) =>
    ` ${count} ${count === 1 ? window.themeStrings.recipe : window.themeStrings.recipes} `;
  const getRecipeTagsFromUrl = (href) => {
    const url = new URL(href, window.location.href);
    const taggedPath = `${recipesPath}/tagged/`;

    if (!url.pathname.startsWith(taggedPath)) return [];

    return url.pathname
      .slice(taggedPath.length)
      .split('+')
      .map((tag) => decodeURIComponent(tag).trim())
      .filter(Boolean);
  };
  const getPageLinks = () =>
    Array.from(
      document.querySelectorAll(
        '#main-blog-filters a[href], .main-blog-recipes__mobile-facets a[href], #BlogRecipeActiveFacetsMobile a[href], .pagination-wrapper a[href]'
      )
    );

  const compareText = (a, b) =>
    (a.dataset.title || '').localeCompare(b.dataset.title || '', undefined, {
      sensitivity: 'base',
      numeric: true,
    });

  const compareBy = (sortBy) => {
    switch (sortBy) {
      case 'created-asc':
        return (a, b) => Number(a.dataset.publishedAt || 0) - Number(b.dataset.publishedAt || 0);
      case 'title-asc':
        return compareText;
      case 'title-desc':
        return (a, b) => compareText(b, a);
      case 'created-desc':
        return (a, b) => Number(b.dataset.publishedAt || 0) - Number(a.dataset.publishedAt || 0);
      case 'popular':
      default:
        return (a, b) => Number(a.dataset.originalIndex || 0) - Number(b.dataset.originalIndex || 0);
    }
  };

  const withSortParam = (href, sortBy = getCurrentSortBy()) => {
    const url = new URL(href, window.location.href);

    if (sortBy === 'popular') {
      url.searchParams.delete('sort_by');
    } else {
      url.searchParams.set('sort_by', sortBy);
    }

    return url;
  };

  const updateUrl = (sortBy) => {
    window.history.replaceState({}, '', withSortParam(window.location.href, sortBy).toString());
  };

  const syncPageLinks = (sortBy = getCurrentSortBy()) => {
    getPageLinks().forEach((link) => {
      const url = withSortParam(link.getAttribute('href'), sortBy);
      link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
    });
  };

  const updateSelectedState = (selectedInput) => {
    const header = document.querySelector('#BlogRecipeSortBy .sort-by--header');

    getOptions().forEach((input) => {
      const item = input.closest('.sort-by--item');
      const isSelected = input === selectedInput;

      input.checked = isSelected;
      if (item) item.classList.toggle('selected', isSelected);
    });

    if (!header || !selectedInput) return;

    const selectedLabel = selectedInput.closest('label');
    const selectedText = selectedLabel ? selectedLabel.textContent.trim() : '';
    const caret = header.querySelector('.icon-caret');

    header.textContent = selectedText;
    if (caret) header.append(caret);
  };

  const applySort = (sortBy = getCurrentSortBy(), shouldUpdateUrl = true) => {
    const articlesGrid = getArticlesGrid();
    const selectedInput = getOptions().find((input) => input.value === sortBy) || getOptions()[0];
    const normalizedSortBy = selectedInput ? selectedInput.value : 'popular';

    if (articlesGrid) {
      getArticles()
        .sort(compareBy(normalizedSortBy))
        .forEach((article) => articlesGrid.append(article));
    }

    updateSelectedState(selectedInput);
    syncPageLinks(normalizedSortBy);
    if (shouldUpdateUrl) updateUrl(normalizedSortBy);
  };

  const replaceIfPresent = (selector, doc) => {
    const current = document.querySelector(selector);
    const next = doc.querySelector(selector);

    if (current && next) current.innerHTML = next.innerHTML;
  };

  const fetchDocument = async (href) => {
    const response = await fetch(href, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    const html = await response.text();

    return new DOMParser().parseFromString(html, 'text/html');
  };

  const scrollToUrlHash = (url) => {
    if (!url.hash) return;

    const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
    if (!target) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({
      top: Math.max(target.getBoundingClientRect().top + window.scrollY - 200, 0),
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  };

  const getPaginationPage = (href) => Number(new URL(href, window.location.href).searchParams.get('page') || 1);

  const getMaxPage = (doc) =>
    Math.max(
      1,
      ...Array.from(doc.querySelectorAll('.pagination-wrapper a[href]'))
        .map((link) => getPaginationPage(link.getAttribute('href')))
        .filter((page) => Number.isFinite(page))
    );

  const withPageParam = (href, page) => {
    const url = new URL(href, window.location.href);

    if (page <= 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', page);
    }

    return url.toString();
  };

  const getArticleKey = (article) => {
    const link = article.querySelector('a[href*="/blogs/"]');
    return link ? new URL(link.getAttribute('href'), window.location.href).pathname : article.dataset.title || '';
  };

  const getSingleTagDocuments = async (tagHandle, sourceUrl) => {
    const url = new URL(sourceUrl, window.location.href);
    url.pathname = `${recipesPath}/tagged/${encodeURIComponent(tagHandle)}`;
    url.searchParams.delete('page');

    const firstDoc = await fetchDocument(url.toString());
    const maxPage = getMaxPage(firstDoc);
    const remainingPages = Array.from({ length: maxPage - 1 }, (_, index) => index + 2);
    const remainingDocs = await Promise.all(remainingPages.map((page) => fetchDocument(withPageParam(url, page))));

    return [firstDoc, ...remainingDocs];
  };

  const renderOrFilteredArticles = async (href) => {
    const tagHandles = getRecipeTagsFromUrl(href);
    if (tagHandles.length < 2) return;

    const docs = (await Promise.all(tagHandles.map((tagHandle) => getSingleTagDocuments(tagHandle, href)))).flat();
    const articlesContainer = document.getElementById('main-blog-articles');
    if (!articlesContainer) return;

    const firstGrid = docs.map((doc) => doc.querySelector('#main-blog-articles .blog-articles')).find(Boolean);
    const seenArticles = new Set();
    const articles = [];

    docs.forEach((doc) => {
      doc.querySelectorAll('#main-blog-articles .blog-articles__article').forEach((article) => {
        const key = getArticleKey(article);
        if (!key || seenArticles.has(key)) return;

        seenArticles.add(key);
        articles.push(article);
      });
    });

    articlesContainer.querySelector('.main-blog-recipes__empty')?.remove();

    let grid = articlesContainer.querySelector('.blog-articles');
    if (!grid) {
      grid = document.createElement('div');
      articlesContainer.append(grid);
    }

    grid.className = firstGrid ? firstGrid.className : 'blog-articles blog-articles--grid';
    grid.innerHTML = '';

    if (articles.length > 0) {
      articles.forEach((article, index) => {
        article.dataset.originalIndex = index;
        grid.append(article);
      });
    } else {
      grid.remove();
      articlesContainer.insertAdjacentHTML(
        'beforeend',
        `<div class="main-blog-recipes__empty title-wrapper center"><h2 class="title title--primary">${window.themeStrings.noRecipes}<br><a href="${recipesPath}" class="underlined-link link">${window.themeStrings.useFewerFilters}</a></h2></div>`
      );
    }

    document.querySelectorAll('#BlogArticleCountDesktop, #BlogArticleCount').forEach((count) => {
      count.textContent = formatRecipeCount(articles.length);
    });

    document.querySelector('.main-blog > .pagination-wrapper')?.remove();
  };

  const syncFilterControls = (doc) => {
    const nextByTag = new Map(
      Array.from(doc.querySelectorAll('[data-recipe-filter-option]')).map((option) => [option.dataset.tagHandle, option])
    );

    document.querySelectorAll('[data-recipe-filter-option]').forEach((option) => {
      const nextOption = nextByTag.get(option.dataset.tagHandle);
      if (!nextOption) return;

      option.setAttribute('href', nextOption.getAttribute('href'));
      option.classList.toggle('active', nextOption.classList.contains('active'));
    });
  };

  const updatePagination = (doc) => {
    const currentPagination = document.querySelector('.main-blog > .pagination-wrapper');
    const nextPagination = doc.querySelector('.main-blog > .pagination-wrapper');
    const mainBlog = document.querySelector('.main-blog');

    if (currentPagination && nextPagination) {
      currentPagination.outerHTML = nextPagination.outerHTML;
      return;
    }

    if (currentPagination && !nextPagination) {
      currentPagination.remove();
      return;
    }

    if (!currentPagination && nextPagination && mainBlog) {
      mainBlog.append(nextPagination.cloneNode(true));
    }
  };

  const setLoading = (loading) => {
    const articles = document.getElementById('main-blog-articles');
    const layout = document.querySelector('.main-blog-recipes__layout');
    const countDesktop = document.getElementById('BlogArticleCountDesktop');
    const countMobile = document.getElementById('BlogArticleCount');
    const countDesktopWrapper = countDesktop ? countDesktop.closest('.product-count-vertical') : null;
    const countMobileWrapper = countMobile ? countMobile.closest('.product-count') : null;
    const loadingSpinners = document.querySelectorAll(
      '.main-blog-recipes__facets-top .loading__spinner, #main-blog-filters .loading__spinner'
    );

    if (articles) articles.classList.toggle('loading', loading);
    if (layout) layout.classList.toggle('loading', loading);
    if (countDesktop) countDesktop.classList.toggle('loading', loading);
    if (countMobile) countMobile.classList.toggle('loading', loading);
    if (countDesktopWrapper) countDesktopWrapper.classList.toggle('loading', loading);
    if (countMobileWrapper) countMobileWrapper.classList.toggle('loading', loading);

    loadingSpinners.forEach((spinner) => {
      spinner.classList.toggle('hidden', !loading);
    });
  };

  const fetchBlogPage = async (href, shouldPush = true) => {
    const url = withSortParam(href);

    if (url.origin !== window.location.origin || !url.pathname.startsWith(recipesPath) || isLoading) return;

    isLoading = true;
    setLoading(true);

    try {
      const doc = await fetchDocument(url.toString());

      replaceIfPresent('#main-blog-articles', doc);
      replaceIfPresent('#BlogRecipeActiveFacets', doc);
      replaceIfPresent('#BlogRecipeActiveFacetsMobile', doc);
      replaceIfPresent('#BlogArticleCountDesktop', doc);
      replaceIfPresent('#BlogArticleCount', doc);
      syncFilterControls(doc);
      updatePagination(doc);

      await renderOrFilteredArticles(url.toString());

      window.history[shouldPush ? 'pushState' : 'replaceState']({}, '', url.toString());
      applySort(url.searchParams.get('sort_by') || 'popular', false);

      const articles = document.getElementById('main-blog-articles');
      if (articles) articles.focus({ preventScroll: true });
      requestAnimationFrame(() => scrollToUrlHash(url));
    } catch (error) {
      window.location.href = url.toString();
    } finally {
      isLoading = false;
      setLoading(false);
    }
  };

  const handleBlogPageLinkClick = (event, shouldStopPropagation = false) => {
    const link = event.target.closest(
      '#main-blog-filters a[href], .main-blog-recipes__mobile-facets a[href], #BlogRecipeActiveFacetsMobile a[href], .pagination-wrapper a[href]'
    );
    if (!link) return;

    const url = new URL(link.getAttribute('href'), window.location.href);
    if (!url.pathname.startsWith(recipesPath)) return;

    event.preventDefault();
    if (shouldStopPropagation) event.stopImmediatePropagation();
    fetchBlogPage(url.toString());
  };

  document.addEventListener(
    'click',
    (event) => {
      if (!event.target.closest('#BlogRecipeActiveFacetsMobile a[href]')) return;
      handleBlogPageLinkClick(event, true);
    },
    true
  );

  document.addEventListener('click', handleBlogPageLinkClick);

  document.addEventListener('change', (event) => {
    if (!event.target.matches('#BlogRecipeSortBy input[name="sort_by"]')) return;

    applySort(event.target.value);
  });

  document.addEventListener('keydown', (event) => {
    const label = event.target.closest('#BlogRecipeSortBy .sort-by--item label');
    const input = label ? label.querySelector('input[name="sort_by"]') : null;

    if (!input || (event.key !== 'Enter' && event.key !== ' ')) return;

    event.preventDefault();
    input.checked = true;
    applySort(input.value);
  });

  window.addEventListener('popstate', () => {
    fetchBlogPage(window.location.href, false);
  });

  if (getRecipeTagsFromUrl(window.location.href).length > 1) {
    fetchBlogPage(window.location.href, false);
  } else {
    applySort(getCurrentSortBy(), false);
  }
})();
